import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { dsoApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { Spinner } from 'react-bootstrap';

/**
 * DSOGuard - Higher Order Component to restrict access to DSO dashboard based on onboarding status
 * Redirects to onboarding page if:
 * 1. Documents are not uploaded (Aadhaar, PAN, business docs)
 * 2. Agreement is not signed
 * 3. DSO is not verified by admin
 */
const DSOGuard = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [isOnboarded, setIsOnboarded] = useState(true);
  const [profile, setProfile] = useState(null);
  const { getCurrentUser } = useAuth();

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      try {
        const response = await dsoApi.getDsoProfile();
        setProfile(response.data);
        
        // Check onboarding conditions
        const dsoProfile = response.data.dso_profile;
        
        // 1. Check if agreement is signed
        const agreementSigned = !!dsoProfile.agreement_signed;
        
        // 2. Check if documents are uploaded
        const docsUploaded = checkDocumentsUploaded(dsoProfile);
        
        // 3. Check if verified by admin
        const isVerified = !!dsoProfile.is_verified;
        
        // Log status for debugging
        console.log("DSO Onboarding Status:", {
          agreementSigned,
          docsUploaded,
          isVerified,
          applicant_type: dsoProfile.applicant_type
        });
        
        // Set onboarding status based on all conditions
        setIsOnboarded(agreementSigned && docsUploaded && isVerified);
      } catch (error) {
        console.error('Error checking onboarding status:', error);
        // If error occurs, redirect to onboarding as a fallback
        setIsOnboarded(false);
      } finally {
        setLoading(false);
      }
    };
    
    checkOnboardingStatus();
  }, []);
  
  // Helper function to check if required documents are uploaded
  const checkDocumentsUploaded = (dsoProfile) => {
    // Determine required documents based on applicant type
    if (dsoProfile.applicant_type === 'Individual') {
      return !!(
        dsoProfile.adhaar_card_front_file && 
        dsoProfile.adhaar_card_back_file && 
        dsoProfile.pan_card_file
      );
    } else if (dsoProfile.applicant_type === 'Business') {
      // For Business profiles, we only need the business documents
      return !!(
        dsoProfile.company_pan_card_file &&
        dsoProfile.company_certificate_file
      );
    }
    
    // Default to requiring personal documents if applicant type is undefined
    return !!(
      dsoProfile.adhaar_card_front_file && 
      dsoProfile.adhaar_card_back_file && 
      dsoProfile.pan_card_file
    );
  };
  
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <Spinner animation="border" />
        <span className="ms-2">Checking onboarding status...</span>
      </div>
    );
  }
  
  // If not onboarded, redirect to onboarding page
  if (!isOnboarded) {
    return <Navigate to="/dso/onboarding" replace />;
  }
  
  // If onboarded, render children
  return children;
};

export default DSOGuard;